import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { ContactGroupType } from 'contacts/enums';
import { useContactGroupActions } from 'contacts/hooks';
import { IContactGroup, IContactGroupAddress } from 'contacts/types';
import { FormProvider } from 'core/states';

import { MergeLocationsForm } from './MergeLocationsForm';
import { mergeLocationsSchema } from './schema';
import { IMergeLocationsForm } from './types';
import { getDefaultValues, getDisabledFields } from './utils';
interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: (updatedLocation: IContactGroupAddress) => void;
  locations: IContactGroupAddress[];
  parentContactGroup: Partial<IContactGroup>;
  contactGroupType: ContactGroupType;
}

export const MergeLocations = ({
  isOpen,
  onClose,
  onSubmitSuccess,
  locations,
  parentContactGroup,
  contactGroupType,
}: IProps) => {
  const { mergeContactGroupAddresses } = useContactGroupActions();

  const parentContactGroupType = useMemo(
    () => parentContactGroup?.type || [],
    [parentContactGroup]
  );

  const onSubmit =
    (reset: UseFormReset<IMergeLocationsForm>) => async (data: IMergeLocationsForm) => {
      const payload = {
        addressIds: locations.map((location) => location.addressId),
        contactGroupId: parentContactGroup?.id,
        data: {
          id: data.name.id,
          ...data.address.value,
          ...(parentContactGroupType.includes(ContactGroupType.CUSTOMER) && {
            externalCustomerNumber: data.externalCustomerNumber?.value,
            accountsReceivableId: data.accountsReceivableId?.value,
            isShipTo: data.isShipTo ? 1 : 0,
            isBillTo: data.isBillTo ? 1 : 0,
          }),
          ...(parentContactGroupType.includes(ContactGroupType.VENDOR) && {
            accountsPayableId: data.accountsPayableId?.value,
          }),
        },
      };

      const { success, contactGroupAddress: updatedLocation } =
        await mergeContactGroupAddresses(payload);

      if (!success) {
        return;
      }

      setTimeout(() => {
        handleOnClose(reset);

        onSubmitSuccess && onSubmitSuccess(updatedLocation);
      }, 1000);
    };

  const handleOnClose = (reset?: UseFormReset<IMergeLocationsForm>) => {
    onClose();
    reset && reset(getDefaultValues(locations, parentContactGroup?.type || [], contactGroupType));
  };

  const requiredFields = useMemo(() => {
    return {
      name: true,
      address: true,
      ...(parentContactGroupType.includes(ContactGroupType.CUSTOMER) && {
        externalCustomerNumber: locations.some((location) => !!location.externalCustomerNumber),
        accountsReceivableId: locations.some((location) => !!location.accountsReceivableId),
      }),
      ...(parentContactGroupType.includes(ContactGroupType.VENDOR) && {
        accountsPayableId: locations.some((location) => !!location.accountsPayableId),
      }),
    };
  }, [parentContactGroupType, locations]);

  return (
    <FormProvider
      schema={mergeLocationsSchema(parentContactGroupType, requiredFields)}
      defaultValues={getDefaultValues(locations, parentContactGroupType, contactGroupType)}
    >
      <MergeLocationsForm
        isOpen={isOpen}
        onSubmit={onSubmit}
        onClose={handleOnClose}
        locations={locations}
        contactGroupTypes={parentContactGroupType}
        disabledFields={getDisabledFields(locations, parentContactGroupType, contactGroupType)}
      />
    </FormProvider>
  );
};
